'use client';

import { createClient } from '@/lib/supabase/client';

export type BookmarkType = 'glossary_term' | 'framework_page' | 'verse_link' | 'names_entry' | 'key_concept' | 'phase_page';

export interface BookmarkItem {
  id: string;
  user_id: string;
  item_type: BookmarkType;
  item_id: string;
  item_title: string;
  item_arabic: string;
  item_href: string;
  item_description: string;
  item_category: string;
  collection_tag: string;
  notes: string;
  created_at: string;
}

export interface BookmarkInput {
  item_type: BookmarkType;
  item_id: string;
  item_title: string;
  item_arabic?: string;
  item_href: string;
  item_description?: string;
  item_category?: string;
  collection_tag?: string;
}

export interface ResonanceSummary {
  topContentTypes: string[];
  topNames: string[];
  topKeys: string[];
  totalInteractions: number;
  role: string;
  interests: string[];
}

const supabase = createClient();

function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem('haal_session') || (() => {
    const id = Math.random().toString(36).slice(2);
    sessionStorage.setItem('haal_session', id);
    return id;
  })();
}

export async function toggleBookmark(input: BookmarkInput): Promise<{ bookmarked: boolean; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { bookmarked: false, error: 'Sign in to save bookmarks' };

  const { data: existing } = await supabase
    .from('bookmarks')
    .select('id')
    .eq('user_id', user.id)
    .eq('item_type', input.item_type)
    .eq('item_id', input.item_id)
    .single();

  if (existing) {
    await supabase.from('bookmarks').delete().eq('id', existing.id);
    return { bookmarked: false };
  } else {
    await supabase.from('bookmarks').insert({
      user_id: user.id,
      item_type: input.item_type,
      item_id: input.item_id,
      item_title: input.item_title,
      item_arabic: input.item_arabic || '',
      item_href: input.item_href,
      item_description: input.item_description || '',
      item_category: input.item_category || '',
      collection_tag: input.collection_tag || 'general',
    });
    return { bookmarked: true };
  }
}

export async function getUserBookmarks(): Promise<BookmarkItem[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data } = await supabase
    .from('bookmarks')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  return data || [];
}

export async function getBookmarkedIds(itemType: BookmarkType): Promise<Set<string>> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return new Set();
  const { data } = await supabase
    .from('bookmarks')
    .select('item_id')
    .eq('user_id', user.id)
    .eq('item_type', itemType);
  return new Set((data || []).map(d => d.item_id));
}

export async function trackResonance(
  contentType: string,
  contentId: string,
  contentTitle: string,
  resonanceType: 'view' | 'bookmark' | 'verse_click' | 'deep_read' | 'search' | 'module_visit' | 'glossary_search' | 'term_expand' | 'role_select' = 'view',
  caretakerRole = ''
) {
  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getSessionId();

  await supabase.from('content_resonance').insert({
    user_id: user?.id || null,
    session_id: sessionId,
    content_type: contentType,
    content_id: contentId,
    content_title: contentTitle,
    resonance_type: resonanceType,
    caretaker_role: caretakerRole || (typeof window !== 'undefined' ? localStorage.getItem('haal_role') || '' : ''),
  });
}

export async function trackModuleVisit(moduleId: string, moduleTitle: string, caretakerRole?: string) {
  await trackResonance(moduleId, moduleId, moduleTitle, 'module_visit', caretakerRole || '');

  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getSessionId();
  const role = caretakerRole || (typeof window !== 'undefined' ? localStorage.getItem('haal_role') || '' : '');

  const { data: existing } = await supabase
    .from('module_visits')
    .select('id, visit_count')
    .eq('session_id', sessionId)
    .eq('module_id', moduleId)
    .single();

  if (existing) {
    await supabase.from('module_visits').update({
      visit_count: (existing.visit_count || 1) + 1,
      last_visited: new Date().toISOString(),
    }).eq('id', existing.id);
  } else {
    await supabase.from('module_visits').insert({
      user_id: user?.id || null,
      session_id: sessionId,
      module_id: moduleId,
      module_title: moduleTitle,
      caretaker_role: role,
      visit_count: 1,
      last_visited: new Date().toISOString(),
    });
  }
}

let glossarySearchTimer: ReturnType<typeof setTimeout> | null = null;
export async function trackGlossarySearch(query: string, resultCount: number, caretakerRole?: string) {
  if (query.length < 3) return;
  if (glossarySearchTimer) clearTimeout(glossarySearchTimer);
  glossarySearchTimer = setTimeout(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const sessionId = getSessionId();
    const role = caretakerRole || (typeof window !== 'undefined' ? localStorage.getItem('haal_role') || '' : '');

    await supabase.from('content_resonance').insert({
      user_id: user?.id || null,
      session_id: sessionId,
      content_type: 'glossary_search',
      content_id: query,
      content_title: `Search: "${query}" (${resultCount} results)`,
      resonance_type: 'search',
      caretaker_role: role,
    });
  }, 800);
}

export async function updateUserProfile(role: string, interests: string[]) {
  const { data: { user } } = await supabase.auth.getUser();
  if (typeof window !== 'undefined') localStorage.setItem('haal_role', role);

  if (!user) return;

  await supabase.from('user_profiles').upsert({
    id: user.id,
    caretaker_role: role,
    interests: interests,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'id' });
}

export async function getUserResonanceSummary(): Promise<ResonanceSummary> {
  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getSessionId();
  const role = typeof window !== 'undefined' ? localStorage.getItem('haal_role') || 'explorer' : 'explorer';

  let query = supabase.from('content_resonance').select('content_type, content_id, content_title');
  if (user) {
    query = query.eq('user_id', user.id);
  } else {
    query = query.eq('session_id', sessionId);
  }

  const { data: resonanceData } = await query;

  const typeCounts: Record<string, number> = {};
  const nameCounts: Record<string, number> = {};
  const keyCounts: Record<string, number> = {};

  (resonanceData || []).forEach(r => {
    typeCounts[r.content_type] = (typeCounts[r.content_type] || 0) + 1;
    if (r.content_type === 'names_entry') nameCounts[r.content_id] = (nameCounts[r.content_id] || 0) + 1;
    if (r.content_type === 'key_concept') keyCounts[r.content_id] = (keyCounts[r.content_id] || 0) + 1;
  });

  const topContentTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
  const topNames = Object.entries(nameCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
  const topKeys = Object.entries(keyCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);

  let interests: string[] = [];
  if (user) {
    const { data: profile } = await supabase.from('user_profiles').select('caretaker_role, interests').eq('id', user.id).single();
    if (profile) {
      interests = profile.interests || [];
    }
  }

  return {
    topContentTypes,
    topNames,
    topKeys,
    totalInteractions: (resonanceData || []).length,
    role,
    interests,
  };
}

export async function trackWalkthroughCheckpoint(
  checkpointId: string,
  checkpointTitle: string,
  checkpointIndex: number,
  totalCheckpoints = 7
) {
  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getSessionId();
  const caretakerRole = typeof window !== 'undefined' ? localStorage.getItem('haal_role') || '' : '';

  await supabase.from('walkthrough_completions').insert({
    user_id: user?.id || null,
    session_id: sessionId,
    caretaker_role: caretakerRole,
    checkpoint_id: checkpointId,
    checkpoint_title: checkpointTitle,
    checkpoint_index: checkpointIndex,
    total_checkpoints: totalCheckpoints,
  });
}
